import { ContractSignatureStatus, ProfileType } from "../__generated__/graphql";
import React, { useMemo } from "react";
import { useTheme } from "../hooks/use-theme";
import { LiftColors } from "../theme";
import { StyleSheet, View, Image, ViewStyle, StyleProp } from "react-native";
import UserAvatar from "../screens/user/UserAvatar";
import Icon from "./Icon";

const AVATAR_SIZE = 48;
const CHECK_SIZE = 24;
const DOT_SIZE = 4;

export type ContractSigningStatusPlus = ContractSignatureStatus | "not_signed";
/**
 * Signature status badge should always be in this order:
 * Talend -> Client -> Lift
 * Independently of the profile type of the user
 */
type SigningStatusBadgeProps = {
    workerAvatarUrl: string;
    clientAvatarUrl: string;
    status: ContractSigningStatusPlus;
    style?: StyleProp<ViewStyle>;
    selfProfileType: ProfileType;
};

type CheckState = "checked" | "unchecked" | "empty" | "error" | "wait";

const BASE_CHECKED_MAP: Record<ContractSigningStatusPlus, [CheckState, CheckState, CheckState]> = {
    canceled_by_lse: ["checked", "checked", "error"],
    canceled_by_other: ["checked", "empty", "error"], // need to be redefined for client and worker
    canceled_by_self: ["error", "empty", "empty"], // need to be redefined for client and worker
    failed: ["error", "empty", "empty"],
    handwritten_in_process: ["wait", "wait", "wait"], // signed on paper, nothing happens in the app
    need_other_sign: ["checked", "unchecked", "unchecked"], // need to be redefined for client and worker
    need_seal: ["checked", "checked", "wait"],
    need_self_sign: ["unchecked", "unchecked", "unchecked"], // need to be redefined for client and worker
    success: ["checked", "checked", "checked"],
    waiting_contract: ["empty", "empty", "empty"],
    waiting_self_sign: ["unchecked", "unchecked", "unchecked"], // need to be redefined for client and worker
    not_signed: ["unchecked", "unchecked", "unchecked"], // used in Create Mission info screen
};

const WORKER_CHECKED_MAP: Record<ContractSigningStatusPlus, [CheckState, CheckState, CheckState]> =
    {
        ...BASE_CHECKED_MAP,
        need_other_sign: ["checked", "wait", "unchecked"], // should never happen
        need_self_sign: ["wait", "unchecked", "unchecked"],
        waiting_self_sign: ["wait", "unchecked", "unchecked"],
        canceled_by_other: ["checked", "error", "empty"],
        canceled_by_self: ["error", "empty", "empty"],
    };

const CLIENT_CHECKED_MAP: Record<ContractSigningStatusPlus, [CheckState, CheckState, CheckState]> =
    {
        ...BASE_CHECKED_MAP,
        need_other_sign: ["wait", "unchecked", "unchecked"],
        need_self_sign: ["checked", "wait", "unchecked"],
        waiting_self_sign: ["checked", "wait", "checked"],
        canceled_by_other: ["error", "empty", "empty"],
        canceled_by_self: ["checked", "error", "empty"],
    };

const CHECK_MAP: Record<CheckState, (colors: LiftColors) => [string, string]> = {
    checked: (colors) => [colors.success, colors.background],
    empty: (colors) => [colors.accentLighter, colors.accentLighter],
    error: (colors) => [colors.error, colors.background],
    unchecked: (colors) => [colors.accentLighter, colors.background],
    wait: (colors) => [colors.secondary, colors.background],
};

const SigningStatusBadge: React.FC<SigningStatusBadgeProps> = ({
    workerAvatarUrl,
    clientAvatarUrl,
    status,
    style,
    selfProfileType,
}) => {
    const styles = useStyles();

    const lseAvatarSource = require("../assets/images/basquiat-avatar.png");

    const [workerChecked, clientChecked, lseChecked] = useMemo(() => {
        if (!status) {
            return ["empty" as CheckState, "empty" as CheckState, "empty" as CheckState];
        }
        return selfProfileType === ProfileType.Client
            ? CLIENT_CHECKED_MAP[status]
            : WORKER_CHECKED_MAP[status];
    }, [status, selfProfileType]);

    return (
        <View style={[styles.container, style]}>
            <View style={styles.avatarCircle}>
                <UserAvatar
                    style={[styles.baseAvatarBadge]}
                    avatarPath={workerAvatarUrl}
                    size={AVATAR_SIZE}
                />
                <CheckBadge state={workerChecked} />
            </View>
            <View style={styles.arrowMiddle}>
                <Icon name="arrow-right" size={16} />
            </View>
            <View style={styles.avatarCircle}>
                <UserAvatar
                    style={[styles.baseAvatarBadge]}
                    avatarPath={clientAvatarUrl}
                    size={AVATAR_SIZE}
                />
                <CheckBadge state={clientChecked} />
            </View>
            <View style={styles.arrowMiddle}>
                <Icon name="arrow-right" size={16} />
            </View>
            <View style={styles.avatarCircle}>
                <Image
                    resizeMode="cover"
                    style={[styles.baseAvatarBadge]}
                    source={lseAvatarSource}
                />
                <CheckBadge state={lseChecked} />
            </View>
        </View>
    );
};

const CheckBadge = ({ state }: { state: CheckState }) => {
    const styles = useStyles();
    const { colors } = useTheme();
    const [color, backgroundColor] = useMemo(() => CHECK_MAP[state](colors), [colors, state]);
    return (
        <View style={[styles.checkBadge, { backgroundColor }]}>
            {state == "unchecked" && (
                <Icon size={CHECK_SIZE - 6} color={color} name="check-heavy" />
            )}
            {state == "checked" && <Icon size={CHECK_SIZE - 3} color={color} name="check-heavy" />}
            {state == "error" && <Icon size={CHECK_SIZE - 5} color={color} name="error-heavy" />}
            {state == "wait" && <Icon size={CHECK_SIZE - 4} color={color} name="wait" />}
            {state == "empty" && <Icon size={CHECK_SIZE - 5} color={color} name="check" />}
        </View>
    );
};

const useStyles = () => {
    const { gridSize, colors } = useTheme();

    return useMemo(
        () =>
            StyleSheet.create({
                container: {
                    flexDirection: "row",
                    alignItems: "center",
                    justifyContent: "center",
                },
                arrowMiddle: {
                    flexGrow: 1,
                    flex: 1,
                    flexDirection: "row",
                    alignItems: "center",
                    justifyContent: "space-around",
                    marginHorizontal: gridSize / 2,
                },
                dot: {
                    height: DOT_SIZE,
                    width: DOT_SIZE,
                    backgroundColor: colors.onSurfaceDisabled,
                    borderRadius: DOT_SIZE / 2,
                },
                avatarCircle: {
                    height: AVATAR_SIZE,
                    width: AVATAR_SIZE,
                    position: "relative",
                },
                baseAvatarBadge: {
                    height: AVATAR_SIZE,
                    width: AVATAR_SIZE,
                    borderRadius: AVATAR_SIZE / 2,
                    borderColor: colors.onSurfaceDisabled,
                    borderWidth: 1,
                },
                checkBadge: {
                    alignItems: "center",
                    justifyContent: "center",
                    height: CHECK_SIZE,
                    width: CHECK_SIZE,
                    borderRadius: CHECK_SIZE / 2,
                    overflow: "hidden",
                    position: "absolute",
                    bottom: -5,
                    right: -5,
                },
                checkIcon: {},
            }),
        [colors, gridSize],
    );
};

export default SigningStatusBadge;
